import { Box } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { MRT_Localization_RU } from 'material-react-table/locales/ru';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { getTableColumnSizingKey } from '@shared/constants/storagesKeysConstants';
import { useTheme } from '@mui/material';
import ToggleMoreFiltersButton from '../ToggleMoreFiltersButton/ToggleMoreFiltersButton';
import { AppLanguage } from '@shared/types';
import UtmsGlobalFilterTextField from '@shared/components/ui/UtmsGlobalFilterTextField';

export const getCustomMuiFilterTextFieldProps = (column, t) => {
    const filterVariant = column.columnDef.filterVariant;
    const isSelect = filterVariant === 'select' || filterVariant === 'multi-select';
    const hasFilter = !!column.getFilterValue();

    return {
        sx: {
            [`& button[aria-label="${t('clear_filter')}"]`]: {
                display: hasFilter ? 'flex' : 'none',
                ...(isSelect && {
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                }),
            },
            ...(isSelect && {
                position: 'relative',
                width: '100%',
                minWidth: 'unset',
            }),
            ...(!isSelect && {
                minWidth: column.getSize() - 22,
                width: 'auto',
            }),
        },
        inputProps: { sx: { height: 25 } },
        SelectProps: {
            sx: {
                height: 34,
                width: '100%',
                '.MuiSelect-select': {
                    pr: isSelect && hasFilter ? '32px !important' : '0 !important',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block !important',
                },
            },
            MenuProps: {
                PaperProps: {
                    sx: { '& .MuiMenuItem-root:first-of-type': { display: 'none', height: 65 } },
                },
            },
        },
        placeholder: t('filter'),
    };
};

const CustomTable = ({
    tableId,
    columns,
    data,
    toolbar,
    additionalToolbar = undefined,
    dialogMode,
    defaultColumn,
    defaultColumnSizing,
    variant = 'outlined',
    state,
    muiTablePaperProps,
    muiTableBodyRowProps,
    ...props
}) => {
    const localStorageKey = getTableColumnSizingKey(tableId);
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [columnSizing, setColumnSizing] = useState(() => {
        const size = localStorage.getItem(localStorageKey);
        return size ? JSON.parse(size) : (defaultColumnSizing ?? {});
    });

    const muiTableCellStyle = { fontSize: 14, whiteSpace: 'wrap' };

    const handleColumnSizingChange = (updater) => {
        const newSize = updater(columnSizing);
        setColumnSizing(newSize);
        localStorageKey && localStorage.setItem(localStorageKey, JSON.stringify(newSize));
    };

    return (
        <MaterialReactTable
            layoutMode="grid"
            enableGlobalFilter
            enablePagination
            enableStickyHeader
            enableStickyFooter
            columns={columns}
            onColumnSizingChange={handleColumnSizingChange}
            data={data}
            initialState={{ showGlobalFilter: true }}
            renderTopToolbar={({ table }) =>
                (toolbar && toolbar(table)) || (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, mt: '1px' }}>
                        <UtmsGlobalFilterTextField table={table} />
                        <ToggleMoreFiltersButton table={table} />
                        {additionalToolbar}
                    </Box>
                )
            }
            enableFilterMatchHighlighting={false}
            manualSorting
            manualFiltering
            manualPagination
            enableColumnResizing={true}
            columnResizeMode="onChange"
            defaultColumn={{
                maxSize: 400,
                minSize: 80,
                size: 160,
                ...defaultColumn?.muiTableHeadCellProps,
                muiTableHeadCellProps: ({ column }) => ({
                    sx: {
                        p: '6px 16px',
                        '.Mui-TableHeadCell-Content-Actions': { mr: '4px' },
                        '.Mui-TableHeadCell-ResizeHandle-Wrapper': {
                            position: 'absolute',
                            right: '-8px',
                            '.MuiDivider-root': {
                                width: '2px',
                                borderWidth: 0,
                                backgroundColor: column.getIsResizing() ? 'transparent' : theme.palette.primary.main,
                            },
                        },
                        pointerEvents: column.getIsResizing() ? 'none' : 'all',
                        ...muiTableCellStyle,
                        ...defaultColumn?.muiTableHeadCellProps?.sx,
                    },
                }),
                ...defaultColumn?.muiTableBodyCellProps,
                muiTableBodyCellProps: {
                    sx: {
                        p: '8px 16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        ...muiTableCellStyle,
                        ...defaultColumn?.muiTableBodyCellProps?.sx,
                    },
                },
                muiEditTextFieldProps: { placeholder: t('filter') },
                muiFilterTextFieldProps: ({ column }) => getCustomMuiFilterTextFieldProps(column, t),
                ...defaultColumn,
            }}
            displayColumnDefOptions={{
                'mrt-row-actions': {
                    size: 32,
                    header: '',
                    muiTableHeadCellProps: { sx: { p: 0 } },
                    muiTableBodyCellProps: { sx: { p: 0, display: 'flex', alignItems: 'flex-start' } },
                },
            }}
            state={{ ...state, density: 'compact', columnSizing }}
            enableHiding={!dialogMode}
            muiTablePaperProps={{
                elevation: 0,
                ...muiTablePaperProps,
                sx: {
                    backgroundColor: 'background.paper',
                    height: '100%',
                    p: variant === 'outlined' ? 2 : undefined,
                    minWidth: 1100,
                    display: 'flex',
                    flexDirection: 'column',
                    ...muiTablePaperProps?.sx,
                },
                variant,
            }}
            muiTableHeadRowProps={{ sx: { boxShadow: 0, backgroundColor: 'background.paper' } }}
            muiTableContainerProps={{
                sx: {
                    maxHeight: 'calc(100% - 10rem)', // 10rem = 3.5rem header + 3.5rem footer height + 3rem toolbar
                    scrollbarGutter: 'stable',
                    height: '100%',
                },
            }}
            getRowId={(row) => row.pk}
            localization={i18n.language === AppLanguage.RU ? MRT_Localization_RU : MRT_Localization_EN}
            muiBottomToolbarProps={{ sx: { boxShadow: 0, backgroundColor: 'background.paper' } }}
            muiTableBodyRowProps={(params) => ({
                hover: false,
                ...(typeof muiTableBodyRowProps === 'function' ? muiTableBodyRowProps(params) : undefined),
                sx: {
                    cursor: 'pointer',
                    backgroundColor: 'transparent !important',
                    ...(typeof muiTableBodyRowProps === 'function'
                        ? muiTableBodyRowProps(params)?.sx
                        : muiTableBodyRowProps?.sx),
                },
            })}
            sortDescFirst={false}
            renderEmptyRowsFallback={() => (
                <Box
                    sx={{
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        verticalAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {t('no_record_to_display')}
                </Box>
            )}
            {...props}
        />
    );
};

export default CustomTable;
